// Reassign the existing setRequestHeader function to
// something else on the XMLHttpRequest class
export const LOCAL_STORAGE_KEY = 'auth0AccessTokenV2';

function getFromLocalStorage(key, src) {
    if (!src && (window?.localStorage ?? false)) {
        return window.localStorage.getItem(key);
    } else {
        return src.getItem(key);
    }
}

export function getToken(lStorage = undefined) {
    return getFromLocalStorage(LOCAL_STORAGE_KEY, lStorage);
}

XMLHttpRequest.prototype.wrappedSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

// Override the existing setRequestHeader function so that it stores the headers
XMLHttpRequest.prototype.setRequestHeader = function (header, value) {
    // Call the wrappedSetRequestHeader function first
    // so, we get exceptions if we are in an error state etc.
    const headerName = header;
    this.wrappedSetRequestHeader(headerName, value);

    // Create a headers map if it does not exist
    if (!this.headers) {
        this.headers = new Map();
    }

    // Create a list for the header that if it does not exist
    if (!this.headers.has(headerName)) {
        this.headers.set(headerName, []);
    }

    // Add the value to the header
    this.headers.get(headerName).push(value);
};

/**
 *
 * @param {string} headerName
 * @param {string} value
 * @param {Map.<string, string>} headers
 * @param {function(*, *): *} setFunction
 * @returns {void}
 */
const setHeaderIfNotSet = (headerName, value, headers, setFunction) => {
    if (!headers.has(headerName)) {
        setFunction(headerName, value);
    }
};

/**
 * Intercepting the send functionality to be able to mangle urls and ensure that authorization tokens are set in
 * legacy code
 */
XMLHttpRequest.prototype.wrappedSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function customSend(body) {
    // handle if the relative path was set
    if (this.isRelativePath) {
        setHeaderIfNotSet(
            'Authorization',
            `Bearer ${getToken()}`,
            this.headers,
            (...args) => this.setRequestHeader(...args)
        );
    }

    // internal v1 call with a string body, check if has data:{} wrapper for body
    if (typeof body === 'string' && this.isRelativePath === true && this.url.startsWith('/api/rest/facade/')) {
        // treat this is a v1 backend dall
        let wasJson, newBody;

        // try to parse body as if it were a JSON string
        try {
            newBody = JSON.parse(body);
            wasJson = true;

            if (typeof newBody.data === 'undefined' || Object.keys(newBody).length > 1) {
                newBody = {data: newBody};
            }
        } catch (err) {
            wasJson = false;
        }

        // when the body contains json, make set appropriate headers
        if (wasJson === true) {
            setHeaderIfNotSet(
                'Content-Type',
                'Application/json',
                this.headers,
                (...args) => this.setRequestHeader(...args)
            );

            setHeaderIfNotSet(
                'Accept',
                'Application/json',
                this.headers,
                (...args) => this.setRequestHeader(...args)
            );
        }

        try {
            return this.wrappedSend(wasJson ? JSON.stringify(newBody) : body);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            return undefined;
        }
    }
    try {
        return this.wrappedSend(body);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
        return undefined;
    }
};

/**
 * Hook function to handle data munging to replace middle tier.
 *
 * @param {XMLHttpRequest} xhr - the xml request.
 * @param {?boolean} [returnAsArray=false] - force return to be in array (even if only one item)
 * @returns {string}
 */
function setupHook(xhr, returnAsArray = false) {
    function getter() {
        delete xhr.responseText;
        const ret = xhr.responseText;
        // eslint-disable-next-line mocha/no-top-level-hooks
        setup();
        try {
            const parsed = JSON.parse(ret);

            if (parsed.data.length > 1) {
                return JSON.stringify(parsed.data);
            }
            if (returnAsArray) {
                return JSON.stringify(parsed.data);
            }
            return JSON.stringify(parsed.data[0]);
        } catch (err) {
            //
        }

        return ret;
    }

    // eslint-disable-next-line no-unused-vars
    function setter(str) {
        //
    }

    function setup() {
        Object.defineProperty(xhr, 'responseText', {
            get: getter,
            set: setter,
            configurable: true,
        });
    }

    // eslint-disable-next-line mocha/no-sibling-hooks,mocha/no-top-level-hooks
    setup();
}

/*
  This is intercepting the open to capture details on the url requested, to be used when settings headers and
  sending the requests
 */
const o = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function () {
    let [, url] = arguments;
    let doIntercept = false,
        returnAsArray = false;

    if (url.startsWith('platform/') || url.startsWith('api/')) {
        url = '/' + url;
    }

    if (url.startsWith('/api/ftptransfer')) {
        doIntercept = false;
        url = url.replace(/^\/api\/ftptransfer/, '/api/rest/facade/ftptransfer');
    } else if (url.startsWith('/api/slicer')) {
        doIntercept = false;
        url = url.replace(/^\/api\/slicer/, '/api/rest/facade/slicer');
    } else if (url.startsWith('/api/user/role-info')) {
        doIntercept = false;
        url = url.replace(/^\/api\/user/, '/api/rest/facade/user');
    } else if (url.startsWith('/api/section-property')) {
        doIntercept = true;
        url = url.replace(/^\/api\/section-property/, '/api/rest/facade/section-property');
    } else if (url.startsWith('/api/metrics')) {
        doIntercept = false;
        url = url.replace(/^\/api\/metrics/, '/api/rest/metrics');
    } else if (url.startsWith('/api/site')) {
        doIntercept = false;
        url = url.replace(/^\/api\/site/, '/api/rest/facade/site');
    } else if (url.startsWith('/api/video/clip')) {
        doIntercept = false;
        url = url.replace(/^\/api\/video\/clip/, '/api/rest/facade/video/clip');
    } else if (url.startsWith('/api/facade_video')) {
        doIntercept = true;
        returnAsArray = true;
        url = url.replace(/^\/api\/facade/, '/api/rest/facade');
    } else if (url.startsWith('/api/facade_navigation_site')) {
        doIntercept = true;
        returnAsArray = true;
        url = url.replace(/^\/api\/facade/, '/api/rest/facade');
    } else if (url.startsWith('/api/facade')) {
        doIntercept = true;
        url = url.replace(/^\/api\/facade/, '/api/rest/facade');
    } else if (url.startsWith('/api') && !url.startsWith('/api/rest')) {
        url = url.replace(/^\/api/, '/api/rest/facade');
        doIntercept = true;
    }

    if (url.includes('platform/facade')) {
        url = url.replace(/platform\/facade/, 'api/rest/facade');
    }

    if (url.includes('assetsByChannel')) {
        doIntercept = false;
    }

    // logic to handle making /api/facade_tag_search => /api/rest/facade/tag/search
    const [pathname] = url.split(/[#?]/g);
    // TODO - revert to '_' when response intercept work is done to handle the response object appropriately
    if (pathname.startsWith('/api/rest') && pathname.includes('_')) {
        url = url.replace(pathname, pathname.replace(/_/g, '/'));
    }

    if (doIntercept && !this._hooked) {
        this._hooked = true;
        this.url = url;
        setupHook(this, returnAsArray);
    }

    // this may need to be after o.apply, trying it this way to reduce duplicate code
    const isRelativePathThatNeedsAuthorization = url.startsWith('/')
        && !url.startsWith('/resources/');

    if (isRelativePathThatNeedsAuthorization) {
        this.isRelativePath = true;
        this.url = url;

        const [, , , queryString] = url.match(/^([^#?]+)((?:#[^?]+)?)((?:\?.*)?)$/);

        if (
            url.startsWith('/api/rest')
            && (queryString.includes('[') || queryString.includes('{'))
            && queryString === decodeURI(queryString)
        ) {
            url = url.replace(queryString, encodeURI(queryString));
        }
    }

    arguments[1] = url;
    const res = o.apply(this, arguments);

    return res;
};
